/* eslint-disable no-undef */
/*
 *
 * Authentication provider
 * */

import { EventType, PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from './authConfig';
import config from './config.json';

const msalInstance = new PublicClientApplication(msalConfig);

// Check if the userSession var is in localStorage; allows deferred login
export const isUserSession = () =>
    localStorage.getItem('userSession') === 'true';

// Return the account object for the currently signed-in user
// if it exists, otherwise null.
export function getUser() {
    const msalAccounts = msalInstance.getAllAccounts();
    if (msalAccounts.length >= 1) {
        return msalAccounts;
    } else return null;
}
export function initMSAL(eventCallback) {
    // Initialize the MSAL instance using promises
    msalInstance
        ?.initialize()
        ?.then(() => {
            msalInstance?.addEventCallback((event) => {
                // Set active account after redirect
                if (
                    (event.eventType === EventType.LOGIN_SUCCESS ||
                        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
                        event.eventType === EventType.SSO_SILENT_SUCCESS) &&
                    event?.payload?.account
                ) {
                    const account = event.payload.account;
                    msalInstance.setActiveAccount(account);
                    eventCallback(event);
                } else if (
                    [
                        EventType.LOGIN_FAILURE,
                        EventType.ACQUIRE_TOKEN_FAILURE,
                        EventType.SSO_SILENT_FAILURE
                    ].includes(event.eventType)
                ) {
                    window.dispatchEvent(new CustomEvent('show-logout-modal'));
                }
            });
        })
        .catch((error) => {
            console.error('MSAL initialization error:', error);
        });

    return msalInstance;
}

export async function getRemoteAccountSession() {
    // If there is no access token in cache, no userSession var in localStorage,
    // try to get the B2C session cookie silently to continue a user session
    if (!getUser() && isUserSession()) {
        // Check the B2C session cookie silently to continue a user session
        // This may fail if third-party cookies are blocked
        try {
            // Check the B2C session cookie
            await msalInstance.ssoSilent({
                scopes: [config.REACT_APP_SCOPE],
                prompt: 'login'
            });
        } catch (error) {
            // Call the popup login method if the third-party cookie method fails
            // This may fail if pop-ups are blocked
            await msalInstance
                .acquireTokenPopup({
                    scopes: [config.REACT_APP_SCOPES],
                    prompt: 'none',
                    popupWindowAttributes: {
                        popupSize: {
                            height: 20,
                            width: 200
                        },
                        popupPosition: {
                            top: 1000,
                            left: 0
                        }
                    }
                })
                .catch(() => {
                    // There may be an error because there is no session, which is
                    // fine for this implementation as that is what we need to know.
                    // If this is the case, then the app will load in an unauthed state
                });
        }
    }
}

export async function getAccessToken() {
    // Ensure MSAL instance is initialized before calling handleRedirectPromise
    return msalInstance
        ?.initialize()
        .then(() => {
            // Handle auth redirect/do all initial setup for MSAL
            return msalInstance
                ?.handleRedirectPromise()
                .then((authResult) => {
                    // Check if user is signed in
                    const account = msalInstance.getActiveAccount();
                    if (!account && !isUserSession()) {
                        // Redirect anonymous user to login page
                        const tokenRequest = {
                            scopes: [config.REACT_APP_SCOPE]
                        };
                        return msalInstance
                            .loginRedirect(tokenRequest)
                            .then((response) => {
                                signIn();
                                return response;
                            })
                            .catch((error) => {
                                console.info('error', error);

                                return null;
                            });
                    } else {
                        const request = {
                            scopes: [config.REACT_APP_SCOPE],
                            account
                        };
                        return msalInstance
                            .acquireTokenSilent(request)
                            .then((response) => {
                                signIn();
                                return response;
                            })
                            .catch((error) => {
                                // If there is a token cache in browser storage and an error,
                                // we should remove userSession var from localStorage and clear
                                // the cache of invalid tokens
                                if (account && error) {
                                    window.dispatchEvent(
                                        new CustomEvent('show-logout-modal')
                                    );
                                    return null;
                                }
                                console.info('error', error);
                                return null;
                            });
                    }
                })
                .catch((err) => {
                    // Handle errors
                    console.error('redirect error', err);
                });
        })
        .catch((error) => {
            console.error('MSAL initialization error:', error);
        });
}

export function signIn() {
    localStorage.setItem('auth-state', 'authenticated');
    localStorage.setItem('userSession', `true`);
}

export async function logout() {
    // lazy require to avoid circular import
    const { persistor } = require('./store');
    await persistor.flush();
    localStorage.removeItem('configHashValue');
    localStorage.removeItem('userSession');
    localStorage.setItem('auth-state', 'unauthenticated');
    msalInstance.logoutRedirect();
}
